import { toast } from '@shared/lib/toast';

// Error handling utilities for React Query
export const errorUtils = {
  // Handle API errors with user-friendly messages
  handleApiError: (error: unknown, defaultMessage: string = 'An error occurred') => {
    if (error instanceof Error) {
      // Check for common error patterns
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        toast.error('Please log in again');
        // Redirect to login if needed (browser only; RN handles 401 via auth state)
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        }
        return;
      }
      
      if (error.message.includes('403') || error.message.includes('Forbidden')) {
        toast.error('You do not have permission to perform this action');
        return;
      }
      
      if (error.message.includes('404') || error.message.includes('Not Found')) {
        toast.error('The requested resource was not found');
        return;
      }
      
      if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
        toast.error('Server error. Please try again later');
        return;
      }
      
      if (error.message.includes('Network Error') || error.message.includes('fetch')) {
        toast.error('Network error. Please check your connection');
        return;
      }
      
      // Show the actual error message if it's user-friendly
      toast.error(error.message);
    } else {
      toast.error(defaultMessage);
    }
  },

  // Handle query errors
  handleQueryError: (error: unknown, queryName: string) => {
    errorUtils.handleApiError(error, `Failed to load ${queryName}`);
  },

  // Handle mutation errors
  handleMutationError: (error: unknown, action: string) => {
    errorUtils.handleApiError(error, `Failed to ${action}`);
  },

  // Retry configuration for different types of operations
  getRetryConfig: (operation: 'query' | 'mutation' | 'auth') => {
    switch (operation) {
      case 'query':
        return {
          retry: 3,
          retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
        };
      case 'mutation':
        return {
          retry: 1,
          retryDelay: 1000,
        };
      case 'auth':
        return {
          retry: false, // Don't retry auth failures
        };
      default:
        return {
          retry: 3,
          retryDelay: 1000,
        };
    }
  },

  // Check if error is retryable
  isRetryableError: (error: unknown): boolean => {
    if (error instanceof Error) {
      // Don't retry auth errors
      if (error.message.includes('401') || error.message.includes('403')) {
        return false;
      }
      
      // Don't retry client errors (4xx except 408, 429)
      if (error.message.includes('400') || error.message.includes('404')) {
        return false;
      }
      
      // Retry network errors and server errors
      if (error.message.includes('Network Error') || 
          error.message.includes('500') || 
          error.message.includes('502') || 
          error.message.includes('503') || 
          error.message.includes('504')) {
        return true;
      }
    }
    
    return true;
  },
};
